import * as React from 'react';

import {Box, Stack, Typography} from '@mui/material';
import {
    AggregationSurveyNpsCounts,
    AggregationSurveyNpsSeries,
} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {useTranslation} from 'react-i18next';

import {CardHeadline} from '../../../insights/kit/Headline';
import {SeriesCard} from '../../../insights/kit/SeriesCard';
import {signed, useInsightsFormatters} from "../../../insights/kit/insightsFormat";
import {InsightsLoading} from '../../../insights/kit/insightsLoading';
import {InsightsColumn, InsightsTable} from '../../../insights/kit/InsightsTable';
import {
    lowerIsBetterFor,
    SENTIMENT_COLORS,
    SentimentChart,
    SentimentKey,
    SentimentLabels,
    SentimentSlice,
    SentimentSpec,
} from '../charts/SentimentChart';

interface NpsSplitCardProps {
    series?: AggregationSurveyNpsSeries;
    loading?: InsightsLoading;
}

const NOTHING: AggregationSurveyNpsCounts = {promoters: 0, passives: 0, detractors: 0};

/*
 * The ring is Sentiment's, the card's neighbour: promoters, passives and detractors take its positive, neutral
 * and negative places, and with them its colours and its reading of a change - more promoters is good news,
 * more detractors bad, passives neither. As on Sentiment, a colour names a class of answer, not a verdict.
 */
const CLASSES: Array<{key: SentimentKey; field: keyof AggregationSurveyNpsCounts}> = [
    {key: 'positive', field: 'promoters'},
    {key: 'neutral', field: 'passives'},
    {key: 'negative', field: 'detractors'},
];

const totalOf = (counts: AggregationSurveyNpsCounts) => counts.promoters + counts.passives + counts.detractors;

const share = (value: number, base: number) => (base === 0 ? 0 : (value / base) * 100);

function toNpsSlices(
    data: AggregationSurveyNpsCounts,
    compareData: AggregationSurveyNpsCounts | undefined,
    labels: Pick<SentimentLabels, SentimentKey>
): SentimentSlice[] {
    const total = totalOf(data);
    const compareTotal = compareData == null ? 0 : totalOf(compareData);

    return CLASSES.map(({key, field}) => {
        const count = data[field];
        const compareCount = compareData?.[field] ?? null;

        const currentShare = share(count, total);
        const compareShare = compareCount == null ? null : share(compareCount, compareTotal);

        return {
            key,
            label: labels[key],
            color: SENTIMENT_COLORS[key],
            count,
            share: currentShare,
            compareCount,
            compareShare,
            delta: compareShare == null ? null : currentShare - compareShare,
        };
    });
}

interface ClassTableProps {
    slices: SentimentSlice[];
    labels: SentimentLabels & {nps: string};
    hasCompare: boolean;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
    formatPoints: (value: number) => string;
}

function ClassTable({slices, labels, hasCompare, formatCount, formatPercent, formatPoints}: ClassTableProps) {
    const columns: Array<InsightsColumn<SentimentSlice> | false> = [
        {
            key: 'class',
            kind: 'label',
            label: labels.nps,
            cell: slice => (
                <Stack direction={'row'} alignItems={'center'} spacing={1}>
                    <Box sx={{width: 10, height: 10, borderRadius: '2px', bgcolor: slice.color, flexShrink: 0}}/>
                    <Box component={'span'}>{slice.label}</Box>
                </Stack>
            ),
        },
        {key: 'count', kind: 'value', label: labels.count, cell: slice => slice.count, format: formatCount},
        {key: 'share', kind: 'detail', label: labels.share, cell: slice => slice.share, format: formatPercent},
        hasCompare && {key: 'compare', kind: 'compare', label: labels.compare, cell: slice => slice.compareCount, format: formatCount},
        hasCompare && {
            key: 'delta',
            kind: 'delta',
            label: labels.delta,
            cell: slice => slice.delta,
            format: formatPoints,
            lowerIsBetter: lowerIsBetterFor,
            color: slice => (slice.key === 'neutral' ? 'text.secondary' : undefined),
            comparison: true,
        },
    ];

    return <InsightsTable label={labels.nps} columns={columns} rows={slices} rowKey={slice => slice.key}/>;
}

export function NpsSplitCard({series, loading}: NpsSplitCardProps) {
    const {t} = useTranslation();

    const data = series?.current ?? NOTHING;
    const compareData = series?.compare ?? undefined;
    const hasCompare = compareData != null;

    const {count: formatCount, percent: formatPercent, points: formatPoints} = useInsightsFormatters();

    const labels = React.useMemo<SentimentLabels>(
        () => ({
            positive: t('Promoters'),
            neutral: t('Passives'),
            negative: t('Detractors'),
            replies: t('answers'),
            count: t('Answers'),
            compare: t('Compare period'),
            share: t('Share'),
            delta: t('Change'),
        }),
        [t],
    );

    const tableLabels = React.useMemo(() => ({nps: t('NPS'), ...labels}), [labels, t]);

    const slices = React.useMemo(() => toNpsSlices(data, compareData, labels), [compareData, data, labels]);

    const classified = totalOf(data);

    // The band's NPS, so the headline and the stat card above it cannot disagree.
    const nps = series?.summary.value ?? null;
    const previousNps = series?.summary.previous ?? null;
    const npsText = (value: number) => signed(value, formatPoints).text;

    const headline = (
        // "+17" alone does not say whether it is many promoters or few detractors: the two shares sit beside it.
        <CardHeadline
            value={nps == null ? '—' : npsText(nps)}
            suffix={classified === 0 ? undefined : t('{{promoters}} promoters − {{detractors}} detractors', {
                promoters: formatPercent(share(data.promoters, classified)),
                detractors: formatPercent(share(data.detractors, classified)),
            })}
            change={previousNps == null ? null : {
                title: t('NPS'),
                current: nps == null ? '—' : npsText(nps),
                previous: npsText(previousNps),
                delta: nps == null ? null : nps - previousNps,
                formatDelta: formatPoints,
            }}
        />
    );

    // Unlike Sentiment, a compare period alone does not draw the rings: with nothing classified now every share is
    // 0 of 0, and the changes against the compare period would read as a collapse that never happened.
    const empty = classified === 0 ? (
        <Typography variant={'body2'} color={'text.secondary'}>
            {t('No NPS answers in this period.')}
        </Typography>
    ) : undefined;

    const spec: SentimentSpec = {slices, labels, formatCount, formatPercent, formatPoints};

    return (
        <SeriesCard
            title={t('NPS split')}
            hint={t('Every answer to a question that counts towards the NPS is a promoter, a passive or a detractor (on a 0 to 10 scale: 9 or 10, 7 or 8, anything lower). The headline is the NPS: the share of promoters minus the share of detractors. The outer ring is this period and the inner ring the one it is compared against.')}
            headline={headline}
            loading={loading}
            empty={empty}
            chart={<SentimentChart {...spec}/>}
            table={(
                <ClassTable
                    slices={slices}
                    labels={tableLabels}
                    hasCompare={hasCompare}
                    formatCount={formatCount}
                    formatPercent={formatPercent}
                    formatPoints={formatPoints}
                />
            )}
        />
    );
}
